import { useMemo, useState, useEffect } from "react";
import { Package } from "lucide-react";
import { FaInstagram, FaYoutube } from 'react-icons/fa';
import currencyData from "../../../data/currency.json";

const TABS = ["All", "Instagram", "YouTube"];

const PLATFORM_META = {
  Instagram: {
    icon: FaInstagram
  },
  YouTube: {
    icon: FaYoutube
  }
};

const currencyLookup = new Map(
  (currencyData?.countries || []).map((country) => [country.code, country])
);

const formatPrice = (amount) =>
  new Intl.NumberFormat("en-US", {
    minimumFractionDigits: amount % 1 === 0 ? 0 : 2,
    maximumFractionDigits: 2
  }).format(amount);

const normalizePackages = (packages) => {
  if (!packages) {
    return { Instagram: [], YouTube: [] };
  }

  const grouped = { Instagram: [], YouTube: [] };

  const appendPackages = (platform, items) => {
    if (!Array.isArray(items) || !grouped[platform]) {
      return;
    }

    const normalizedItems = items
      .map((item, index) => {
        const currencyCode = item?.currencyCode || item?.currency || "";
        const currencyMeta = currencyLookup.get(currencyCode);
        const price = Number(item?.price);

        if (Number.isNaN(price) || price <= 0) {
          return null;
        }

        return {
          id: item?.id || `${platform.toLowerCase()}-${index}`,
          title: item?.title || `${platform} Package`,
          description: item?.description || "",
          platform,
          price,
          currencyCode,
          currencySymbol: item?.currencySymbol || currencyMeta?.symbol || "$",
          source: item?.source || "package"
        };
      })
      .filter(Boolean)
      .sort((a, b) => a.price - b.price);

    grouped[platform] = [...grouped[platform], ...normalizedItems].sort(
      (a, b) => a.price - b.price
    );
  };

  if (Array.isArray(packages)) {
    packages.forEach((item, index) => {
      const platform =
        item?.platform === "instagram" || item?.platform === "Instagram"
          ? "Instagram"
          : item?.platform === "youtube" || item?.platform === "YouTube"
            ? "YouTube"
            : null;

      if (!platform) {
        return;
      }

      appendPackages(platform, [
        {
          ...item,
          id: item?.id || `${platform.toLowerCase()}-${index}`
        }
      ]);
    });

    return grouped;
  }

  appendPackages("Instagram", packages.Instagram || packages.instagram);
  appendPackages("YouTube", packages.YouTube || packages.youtube);

  return grouped;
};

const Packages = ({ packages = {} }) => {
  const [activeTab, setActiveTab] = useState("All");
  const [expandedDescriptions, setExpandedDescriptions] = useState({});

  const normalizedPackages = useMemo(() => normalizePackages(packages), [packages]);
  const [charLimit, setCharLimit] = useState(50);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 640) {
        setCharLimit(20); // mobile
      } else {
        setCharLimit(50); // tablet + desktop
      }
    };

    handleResize();
    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const allPackages = useMemo(
    () => [...normalizedPackages.Instagram, ...normalizedPackages.YouTube],
    [normalizedPackages]
  );

  const visiblePackages = useMemo(() => {
    if (activeTab === "All") {
      return allPackages;
    }

    return normalizedPackages[activeTab] || [];
  }, [activeTab, allPackages, normalizedPackages]);

  const hasAnyPackages = allPackages.length > 0;
  const visibleTabs = useMemo(
    () =>
      TABS.filter(
        (tab) => tab === "All" || (normalizedPackages[tab] && normalizedPackages[tab].length > 0)
      ),
    [normalizedPackages]
  );

  const toggleDescription = (packageKey) => {
    setExpandedDescriptions((current) => ({
      ...current,
      [packageKey]: !current[packageKey]
    }));
  };

  return (
    <section>
      <div className="mb-4 md:mb-6">
        <h2 className="md:text-2xl text-xl font-semibold text-gray-900">Packages</h2>
      </div>

      <div>
        <div className="border-b border-gray-300 mb-4 md:mb-6">
          <div className="flex items-center gap-6 overflow-x-auto scrollbar-hide">
            {visibleTabs.map((tab) => (
              <button
                key={tab}
                type="button"
                onClick={() => setActiveTab(tab)}
                className={`whitespace-nowrap border-b-[3px] pb-3 text-sm md:text-lg font-semibold transition ${activeTab === tab
                    ? "border-black text-black"
                    : "border-transparent text-gray-500 hover:text-gray-800"
                  }`}
              >
                {tab}
              </button>
            ))}
          </div>
        </div>

        {!hasAnyPackages ? (
          <div className="rounded-3xl border border-gray-200 bg-white px-6 py-14 text-center">
            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-gray-100 text-gray-400">
              <Package size={28} />
            </div>
            <h3 className="text-base font-semibold text-gray-900">No packages available yet</h3>
            <p className="mt-2 text-sm text-gray-500">
              Verified creator rates will appear here once service pricing has been added.
            </p>
          </div>
        ) : visiblePackages.length === 0 ? (
          <div className="rounded-3xl border border-gray-200 bg-white px-6 py-14 text-center">
            <p className="text-sm text-gray-500">No packages available for {activeTab} right now.</p>
          </div>
        ) : (
          <div className="space-y-5">
            {visiblePackages.map((pkg) => {
              const meta = PLATFORM_META[pkg.platform];
              const Icon = meta.icon;
              const packageKey = `${pkg.platform}-${pkg.id}`;
              const description = pkg.description || "Collaboration pricing shared by the creator.";
              const isExpanded = Boolean(expandedDescriptions[packageKey]);
              const shouldClamp = description.length > charLimit;
              const visibleDescription =
                shouldClamp && !isExpanded
                  ? `${description.slice(0, charLimit).trimEnd()}...`
                  : description;

              return (
                <article
                  key={packageKey}
                  className="rounded-xl border border-zinc-300 bg-white p-3 md:p-6 shadow-sm hover:shadow-md transition"
                >
                  <div className="flex items-start gap-4">

                    {/* Icon */}
                    <div className="flex h-6 w-6 md:h-10 md:w-10 shrink-0 items-center justify-center rounded-xl bg-red-100 text-red-500">
                      <Icon size={16} />
                    </div>

                    <div className="flex-1 min-w-0">

                      {/* Top Row */}
                      <div className="flex items-start justify-between gap-3">

                        {/* Title */}
                        <h3 className="text-sm md:text-lg lg:text-xl font-semibold text-gray-900 leading-tight">
                          {pkg.title}
                        </h3>

                        {/* Price + Radio */}
                        <div className="flex items-center gap-3 shrink-0">
                          <div className="text-right">
                            <p className="text-sm md:text-lg lg:text-xl font-semibold text-gray-900 leading-none">
                              {pkg.currencySymbol}
                              {formatPrice(pkg.price)}
                            </p>

                            {pkg.currencyCode && (
                              <p className="text-xs md:text-xs uppercase tracking-widest text-gray-500">
                                {pkg.currencyCode}
                              </p>
                            )}
                          </div>

                          {/* Radio */}
                          <span className="h-5 w-5 md:h-8 md:w-8 rounded-full border-2 border-gray-300 bg-white flex-shrink-0" />
                        </div>
                      </div>

                      {/* Description */}
                      <p className="mt-2 text-xs md:text-sm leading-relaxed text-gray-500">
                        {visibleDescription}
                        {shouldClamp && (
                          <>
                            {" "}
                            <button
                              type="button"
                              onClick={() => toggleDescription(packageKey)}
                              className="font-medium text-gray-900 underline underline-offset-2"
                            >
                              {isExpanded ? "See Less" : "See More"}
                            </button>
                          </>
                        )}
                      </p>

                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
};

export default Packages; 
